import { ref } from 'vue'
import { db } from '@/firebase/firebase'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  increment,
  writeBatch
} from 'firebase/firestore'

// 카테고리 정의
export const COMMUNITY_CATEGORIES = [
  { id: 'all', label: '전체', icon: 'grid' },
  { id: 'free', label: '자유', icon: 'chat' },
  { id: 'question', label: '질문', icon: 'question' },
  { id: 'review', label: '분양후기', icon: 'star' },
  { id: 'info', label: '정보공유', icon: 'info' },
  { id: 'tip', label: '꿀팁', icon: 'bulb' }
]

// 신고 사유 정의
export const REPORT_REASONS = [
  { id: 'spam', label: '스팸/광고' },
  { id: 'abuse', label: '욕설/비방' },
  { id: 'adult', label: '음란물' },
  { id: 'illegal', label: '불법 정보' },
  { id: 'duplicate', label: '도배' },
  { id: 'other', label: '기타' }
]

// 블라인드 처리 기준 (신고 횟수)
export const BLIND_THRESHOLD = 3

export function useCommunity() {
  const loading = ref(false)
  const error = ref(null)

  // 이메일 마스킹 (kim****@naver.com)
  const maskEmail = (email) => {
    if (!email) return '익명'
    const [local, domain] = email.split('@')
    if (!domain) return '익명'

    const visiblePart = local.slice(0, 3)
    return `${visiblePart}****@${domain}`
  }

  // 시간 포맷 (N분 전, N시간 전, N일 전)
  const formatTimeAgo = (dateString) => {
    if (!dateString) return ''

    const now = new Date()
    const date = new Date(dateString)
    const diffMs = now - date
    const diffSec = Math.floor(diffMs / 1000)
    const diffMin = Math.floor(diffSec / 60)
    const diffHour = Math.floor(diffMin / 60)
    const diffDay = Math.floor(diffHour / 24)

    if (diffMin < 1) return '방금 전'
    if (diffMin < 60) return `${diffMin}분 전`
    if (diffHour < 24) return `${diffHour}시간 전`
    if (diffDay < 7) return `${diffDay}일 전`

    // 7일 이상이면 날짜 표시
    const month = date.getMonth() + 1
    const day = date.getDate()
    return `${month}/${day}`
  }

  // 게시글 목록 조회 (페이지네이션)
  const getPosts = async (options = {}) => {
    loading.value = true
    error.value = null

    const {
      category = 'all',
      pageSize = 20,
      lastDoc = null,
      searchQuery = ''
    } = options

    try {
      let q

      if (category === 'all') {
        q = query(
          collection(db, 'communityPosts'),
          orderBy('createdAt', 'desc'),
          limit(pageSize)
        )
      } else {
        q = query(
          collection(db, 'communityPosts'),
          where('category', '==', category),
          orderBy('createdAt', 'desc'),
          limit(pageSize)
        )
      }

      // 페이지네이션
      if (lastDoc) {
        if (category === 'all') {
          q = query(
            collection(db, 'communityPosts'),
            orderBy('createdAt', 'desc'),
            startAfter(lastDoc),
            limit(pageSize)
          )
        } else {
          q = query(
            collection(db, 'communityPosts'),
            where('category', '==', category),
            orderBy('createdAt', 'desc'),
            startAfter(lastDoc),
            limit(pageSize)
          )
        }
      }

      const snapshot = await getDocs(q)
      let posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        maskedEmail: maskEmail(doc.data().userEmail),
        timeAgo: formatTimeAgo(doc.data().createdAt)
      }))

      // 검색어 필터링 (클라이언트 사이드)
      if (searchQuery) {
        const lowerQuery = searchQuery.toLowerCase()
        posts = posts.filter(post =>
          post.title?.toLowerCase().includes(lowerQuery) ||
          post.content?.toLowerCase().includes(lowerQuery)
        )
      }

      return {
        posts,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === pageSize
      }
    } catch (err) {
      console.error('게시글 목록 조회 실패:', err)
      error.value = err.message
      return { posts: [], lastDoc: null, hasMore: false }
    } finally {
      loading.value = false
    }
  }

  // 게시글 상세 조회
  const getPost = async (postId) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'communityPosts', postId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('게시글을 찾을 수 없습니다.')
      }

      // 조회수 증가
      await updateDoc(docRef, {
        viewCount: increment(1)
      })

      const data = docSnap.data()
      return {
        id: docSnap.id,
        ...data,
        maskedEmail: maskEmail(data.userEmail),
        timeAgo: formatTimeAgo(data.createdAt),
        viewCount: (data.viewCount || 0) + 1
      }
    } catch (err) {
      console.error('게시글 조회 실패:', err)
      error.value = err.message
      return null
    } finally {
      loading.value = false
    }
  }

  // 게시글 작성
  const createPost = async (postData) => {
    loading.value = true
    error.value = null

    try {
      const docRef = await addDoc(collection(db, 'communityPosts'), {
        title: postData.title,
        content: postData.content,
        category: postData.category,
        userId: postData.userId,
        userEmail: postData.userEmail,
        userName: postData.userName || '',
        viewCount: 0,
        likeCount: 0,
        commentCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      return { success: true, id: docRef.id }
    } catch (err) {
      console.error('게시글 작성 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 게시글 수정
  const updatePost = async (postId, postData) => {
    loading.value = true
    error.value = null

    try {
      await updateDoc(doc(db, 'communityPosts', postId), {
        title: postData.title,
        content: postData.content,
        category: postData.category,
        updatedAt: new Date().toISOString()
      })

      return { success: true }
    } catch (err) {
      console.error('게시글 수정 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 게시글 삭제
  const deletePost = async (postId) => {
    loading.value = true
    error.value = null

    try {
      const batch = writeBatch(db)

      // 게시글 삭제
      batch.delete(doc(db, 'communityPosts', postId))

      // 댓글도 함께 삭제
      const commentsQuery = query(
        collection(db, 'communityComments'),
        where('postId', '==', postId)
      )
      const commentsSnapshot = await getDocs(commentsQuery)
      commentsSnapshot.docs.forEach(commentDoc => {
        batch.delete(doc(db, 'communityComments', commentDoc.id))
      })

      // 좋아요도 함께 삭제
      const likesQuery = query(
        collection(db, 'communityLikes'),
        where('postId', '==', postId)
      )
      const likesSnapshot = await getDocs(likesQuery)
      likesSnapshot.docs.forEach(likeDoc => {
        batch.delete(doc(db, 'communityLikes', likeDoc.id))
      })

      await batch.commit()

      return { success: true }
    } catch (err) {
      console.error('게시글 삭제 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 댓글 목록 조회
  const getComments = async (postId) => {
    try {
      const q = query(
        collection(db, 'communityComments'),
        where('postId', '==', postId),
        where('parentId', '==', null),
        orderBy('createdAt', 'asc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        maskedEmail: maskEmail(doc.data().userEmail),
        timeAgo: formatTimeAgo(doc.data().createdAt)
      }))
    } catch (err) {
      console.error('댓글 조회 실패:', err)
      return []
    }
  }

  // 대댓글 조회
  const getReplies = async (parentId) => {
    try {
      const q = query(
        collection(db, 'communityComments'),
        where('parentId', '==', parentId),
        orderBy('createdAt', 'asc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        maskedEmail: maskEmail(doc.data().userEmail),
        timeAgo: formatTimeAgo(doc.data().createdAt)
      }))
    } catch (err) {
      console.error('대댓글 조회 실패:', err)
      return []
    }
  }

  // 댓글 작성
  const createComment = async (commentData) => {
    loading.value = true
    error.value = null

    try {
      const docRef = await addDoc(collection(db, 'communityComments'), {
        postId: commentData.postId,
        userId: commentData.userId,
        userEmail: commentData.userEmail,
        content: commentData.content,
        parentId: commentData.parentId || null,
        replyCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // 부모 댓글이 있으면 replyCount 증가
      if (commentData.parentId) {
        await updateDoc(doc(db, 'communityComments', commentData.parentId), {
          replyCount: increment(1),
          updatedAt: new Date().toISOString()
        })
      }

      // 게시글의 commentCount 증가
      await updateDoc(doc(db, 'communityPosts', commentData.postId), {
        commentCount: increment(1),
        updatedAt: new Date().toISOString()
      })

      return { success: true, id: docRef.id }
    } catch (err) {
      console.error('댓글 작성 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 댓글 삭제
  const deleteComment = async (commentId, postId, parentId = null) => {
    loading.value = true
    error.value = null

    try {
      let deleteCount = 1

      // 대댓글인 경우 부모의 replyCount 감소
      if (parentId) {
        await updateDoc(doc(db, 'communityComments', parentId), {
          replyCount: increment(-1),
          updatedAt: new Date().toISOString()
        })
      } else {
        // 부모 댓글 삭제 시 대댓글도 모두 삭제
        const repliesQuery = query(
          collection(db, 'communityComments'),
          where('parentId', '==', commentId)
        )
        const repliesSnapshot = await getDocs(repliesQuery)

        deleteCount += repliesSnapshot.size

        for (const replyDoc of repliesSnapshot.docs) {
          await deleteDoc(doc(db, 'communityComments', replyDoc.id))
        }
      }

      await deleteDoc(doc(db, 'communityComments', commentId))

      // 게시글의 commentCount 감소
      await updateDoc(doc(db, 'communityPosts', postId), {
        commentCount: increment(-deleteCount),
        updatedAt: new Date().toISOString()
      })

      return { success: true }
    } catch (err) {
      console.error('댓글 삭제 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 좋아요 토글
  const toggleLike = async (postId, userId) => {
    try {
      // 이미 좋아요 했는지 확인
      const likeQuery = query(
        collection(db, 'communityLikes'),
        where('postId', '==', postId),
        where('userId', '==', userId)
      )
      const likeSnapshot = await getDocs(likeQuery)

      if (likeSnapshot.empty) {
        // 좋아요 추가
        await addDoc(collection(db, 'communityLikes'), {
          postId,
          userId,
          createdAt: new Date().toISOString()
        })

        await updateDoc(doc(db, 'communityPosts', postId), {
          likeCount: increment(1)
        })

        return { success: true, liked: true }
      } else {
        // 좋아요 취소
        await deleteDoc(doc(db, 'communityLikes', likeSnapshot.docs[0].id))

        await updateDoc(doc(db, 'communityPosts', postId), {
          likeCount: increment(-1)
        })

        return { success: true, liked: false }
      }
    } catch (err) {
      console.error('좋아요 토글 실패:', err)
      return { success: false, error: err.message }
    }
  }

  // 좋아요 여부 확인
  const checkLiked = async (postId, userId) => {
    if (!userId) return false

    try {
      const likeQuery = query(
        collection(db, 'communityLikes'),
        where('postId', '==', postId),
        where('userId', '==', userId)
      )
      const likeSnapshot = await getDocs(likeQuery)

      return !likeSnapshot.empty
    } catch (err) {
      console.error('좋아요 확인 실패:', err)
      return false
    }
  }

  // 인기글 조회
  const getPopularPosts = async (limitCount = 5) => {
    try {
      const q = query(
        collection(db, 'communityPosts'),
        orderBy('likeCount', 'desc'),
        limit(limitCount)
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        maskedEmail: maskEmail(doc.data().userEmail),
        timeAgo: formatTimeAgo(doc.data().createdAt)
      }))
    } catch (err) {
      console.error('인기글 조회 실패:', err)
      return []
    }
  }

  // ==================== 북마크 기능 ====================

  // 북마크 토글
  const toggleBookmark = async (postId, userId) => {
    try {
      const bookmarkQuery = query(
        collection(db, 'communityBookmarks'),
        where('postId', '==', postId),
        where('userId', '==', userId)
      )
      const snapshot = await getDocs(bookmarkQuery)

      if (snapshot.empty) {
        // 북마크 추가
        await addDoc(collection(db, 'communityBookmarks'), {
          postId,
          userId,
          createdAt: new Date().toISOString()
        })

        // 게시글의 bookmarkCount 증가
        await updateDoc(doc(db, 'communityPosts', postId), {
          bookmarkCount: increment(1)
        })

        return { success: true, bookmarked: true }
      } else {
        // 북마크 취소
        await deleteDoc(doc(db, 'communityBookmarks', snapshot.docs[0].id))

        await updateDoc(doc(db, 'communityPosts', postId), {
          bookmarkCount: increment(-1)
        })

        return { success: true, bookmarked: false }
      }
    } catch (err) {
      console.error('북마크 토글 실패:', err)
      return { success: false, error: err.message }
    }
  }

  // 북마크 여부 확인
  const checkBookmarked = async (postId, userId) => {
    if (!userId) return false

    try {
      const bookmarkQuery = query(
        collection(db, 'communityBookmarks'),
        where('postId', '==', postId),
        where('userId', '==', userId)
      )
      const snapshot = await getDocs(bookmarkQuery)
      return !snapshot.empty
    } catch (err) {
      console.error('북마크 확인 실패:', err)
      return false
    }
  }

  // 내 북마크 목록 조회
  const getMyBookmarks = async (userId, pageSize = 20) => {
    try {
      const q = query(
        collection(db, 'communityBookmarks'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      )

      const snapshot = await getDocs(q)
      const bookmarks = snapshot.docs.map(doc => doc.data())

      // 게시글 정보 가져오기
      const posts = []
      for (const bookmark of bookmarks) {
        const postDoc = await getDoc(doc(db, 'communityPosts', bookmark.postId))
        if (postDoc.exists() && !postDoc.data().isBlinded) {
          posts.push({
            id: postDoc.id,
            ...postDoc.data(),
            maskedEmail: maskEmail(postDoc.data().userEmail),
            timeAgo: formatTimeAgo(postDoc.data().createdAt),
            bookmarkedAt: bookmark.createdAt
          })
        }
      }

      return posts
    } catch (err) {
      console.error('북마크 목록 조회 실패:', err)
      return []
    }
  }

  // ==================== 신고 기능 ====================

  // 게시글 신고
  const reportPost = async (reportData) => {
    loading.value = true
    error.value = null

    try {
      const { postId, userId, userEmail, reason, reasonDetail } = reportData

      // 이미 신고했는지 확인
      const existingQuery = query(
        collection(db, 'communityReports'),
        where('postId', '==', postId),
        where('reporterId', '==', userId),
        where('type', '==', 'post')
      )
      const existingSnapshot = await getDocs(existingQuery)

      if (!existingSnapshot.empty) {
        return { success: false, error: '이미 신고한 게시글입니다.' }
      }

      // 신고 추가
      await addDoc(collection(db, 'communityReports'), {
        type: 'post',
        postId,
        commentId: null,
        reporterId: userId,
        reporterEmail: userEmail,
        reason,
        reasonDetail: reasonDetail || '',
        status: 'pending', // pending, resolved, dismissed
        createdAt: new Date().toISOString()
      })

      // 게시글의 reportCount 증가
      const postRef = doc(db, 'communityPosts', postId)
      await updateDoc(postRef, {
        reportCount: increment(1)
      })

      // 신고 횟수 확인 후 블라인드 처리
      const postDoc = await getDoc(postRef)
      const reportCount = (postDoc.data().reportCount || 0)

      if (reportCount >= 3) {
        await updateDoc(postRef, {
          isBlinded: true,
          blindedAt: new Date().toISOString()
        })
        return { success: true, blinded: true }
      }

      return { success: true, blinded: false }
    } catch (err) {
      console.error('신고 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 댓글 신고
  const reportComment = async (reportData) => {
    loading.value = true
    error.value = null

    try {
      const { commentId, postId, userId, userEmail, reason, reasonDetail } = reportData

      // 이미 신고했는지 확인
      const existingQuery = query(
        collection(db, 'communityReports'),
        where('commentId', '==', commentId),
        where('reporterId', '==', userId),
        where('type', '==', 'comment')
      )
      const existingSnapshot = await getDocs(existingQuery)

      if (!existingSnapshot.empty) {
        return { success: false, error: '이미 신고한 댓글입니다.' }
      }

      // 신고 추가
      await addDoc(collection(db, 'communityReports'), {
        type: 'comment',
        postId,
        commentId,
        reporterId: userId,
        reporterEmail: userEmail,
        reason,
        reasonDetail: reasonDetail || '',
        status: 'pending',
        createdAt: new Date().toISOString()
      })

      // 댓글의 reportCount 증가
      const commentRef = doc(db, 'communityComments', commentId)
      await updateDoc(commentRef, {
        reportCount: increment(1)
      })

      // 신고 횟수 확인 후 블라인드 처리
      const commentDoc = await getDoc(commentRef)
      const reportCount = (commentDoc.data().reportCount || 0)

      if (reportCount >= 3) {
        await updateDoc(commentRef, {
          isBlinded: true,
          blindedAt: new Date().toISOString()
        })
        return { success: true, blinded: true }
      }

      return { success: true, blinded: false }
    } catch (err) {
      console.error('댓글 신고 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // ==================== 내 활동 조회 ====================

  // 내가 쓴 글 조회
  const getMyPosts = async (userId, pageSize = 20) => {
    try {
      const q = query(
        collection(db, 'communityPosts'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        maskedEmail: maskEmail(doc.data().userEmail),
        timeAgo: formatTimeAgo(doc.data().createdAt)
      }))
    } catch (err) {
      console.error('내 글 조회 실패:', err)
      return []
    }
  }

  // 내가 쓴 댓글 조회
  const getMyComments = async (userId, pageSize = 20) => {
    try {
      const q = query(
        collection(db, 'communityComments'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      )

      const snapshot = await getDocs(q)
      const comments = []

      for (const commentDoc of snapshot.docs) {
        const commentData = commentDoc.data()

        // 게시글 정보도 함께 가져오기
        const postDoc = await getDoc(doc(db, 'communityPosts', commentData.postId))
        const postData = postDoc.exists() ? postDoc.data() : null

        comments.push({
          id: commentDoc.id,
          ...commentData,
          maskedEmail: maskEmail(commentData.userEmail),
          timeAgo: formatTimeAgo(commentData.createdAt),
          postTitle: postData?.title || '삭제된 게시글',
          postIsBlinded: postData?.isBlinded || false
        })
      }

      return comments
    } catch (err) {
      console.error('내 댓글 조회 실패:', err)
      return []
    }
  }

  // 내가 좋아요한 글 조회
  const getMyLikedPosts = async (userId, pageSize = 20) => {
    try {
      const q = query(
        collection(db, 'communityLikes'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      )

      const snapshot = await getDocs(q)
      const posts = []

      for (const likeDoc of snapshot.docs) {
        const likeData = likeDoc.data()
        const postDoc = await getDoc(doc(db, 'communityPosts', likeData.postId))

        if (postDoc.exists() && !postDoc.data().isBlinded) {
          posts.push({
            id: postDoc.id,
            ...postDoc.data(),
            maskedEmail: maskEmail(postDoc.data().userEmail),
            timeAgo: formatTimeAgo(postDoc.data().createdAt),
            likedAt: likeData.createdAt
          })
        }
      }

      return posts
    } catch (err) {
      console.error('좋아요 목록 조회 실패:', err)
      return []
    }
  }

  return {
    loading,
    error,
    maskEmail,
    formatTimeAgo,
    COMMUNITY_CATEGORIES,
    getPosts,
    getPost,
    createPost,
    updatePost,
    deletePost,
    getComments,
    getReplies,
    createComment,
    deleteComment,
    toggleLike,
    checkLiked,
    getPopularPosts,
    // 북마크
    toggleBookmark,
    checkBookmarked,
    getMyBookmarks,
    // 신고
    reportPost,
    reportComment,
    // 내 활동
    getMyPosts,
    getMyComments,
    getMyLikedPosts
  }
}
